import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators';
import { Events } from '../../events/app-events';
import { DepInjMixin, requires } from '../../mixins/dependency';
import { MobileDeviceService } from '../../services/mobile-device';
import { QuestionsService } from '../../services/questions-service';
import { UserService } from '../../services/user-service';
import { commonLHCOmponentStyle } from '../../styles/common-style';
import { eventBus } from '../../utils/event-bus';
import { DeviceAwareComponent } from '../components/device-aware-component';
import lhAppStyleDesktop from './lh-app.style.desktop';
import lhAppStyleMobile from './lh-app.style.mobile';

type Deps = {
  userService: UserService;
  mobileDeviceService: MobileDeviceService;
  questionsService: QuestionsService;
};

@requires( UserService.serviceName, MobileDeviceService.serviceName, QuestionsService.serviceName )
@customElement('lh-app')
export class LHApp extends DepInjMixin<Deps>(DeviceAwareComponent) {
  static get baseStyles() {
    return [
      commonLHCOmponentStyle,
      css`
        :host {
          height: 100%;
        }
      :host(.in-transition) {
        pointer-events: none;
      }
    `,
    ];
  };
  static get desktopStyles() {
    return [lhAppStyleDesktop];
  };
  static get mobileStyles() {
    return [lhAppStyleMobile];
  };

  @property({ attribute: 'current-section', reflect: true, type: String })
    currentSectionId = '';

  @property({ attribute: 'display-mode', reflect: true, type: String })
    displayMode: string = 'section';

  @property({ attribute: 'keyboard-open', reflect: true, type: Boolean })
  public isKeyboardOpen = false;
  private unsubToMobileKeyboardEvent: Function | undefined;
  private unsubToSectionChangeEvent: Function | undefined;

  handleClick(e: any) { eventBus().emit('close-all-tooltips', e); };

  connectedCallback() {
    super.connectedCallback();

    /** Any Click Event */
    window.addEventListener('click', (event) => { this.handleClick(event); });

    /** Checking if mobile keyboard is open */
    this.unsubToMobileKeyboardEvent = this.deps.mobileDeviceService
      .getBus()
      .on<boolean>(Events.Type.KeyboardStateChanged, (state) => {
        this.isKeyboardOpen = state;
      });

    /** Section changed events */
    this.unsubToSectionChangeEvent = eventBus().on(
      Events.Type.SectionChanged,
      ({ sectionId }: { sectionId: string; }) => {
        const sectionName = sectionId;
        if (this.deps.questionsService.model.getSections().length <= 1) return;
        this.classList.remove('header-high');
        this.classList.add('section-changing');
        this.classList.add('in-transition');
        setTimeout(() => {
          this.classList.remove('section-changing');
          setTimeout(() => this.classList.remove('in-transition'), 100);
        }, 100);
        if (sectionName === 'S_Summary') {
          this.displayMode = 'summary';
        } else {
          this.displayMode = 'section';
        }
        this.currentSectionId = sectionName;
      },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubToMobileKeyboardEvent?.();
    this.unsubToSectionChangeEvent?.();
  }

  renderContent() {
    if (this.deps.questionsService.model.getSections().length <= 1) {
      this.classList.add('header-high');
    }
    return html`<slot></slot>`;
  }
}
